import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ DOC-01, FND-04 and JOB-09 at v1.959. A document is rendered and kept by the API, then opened
// through a link that lives five minutes (ADR-0011): asked for at the moment of opening, never kept.

export type StoredFile = components['schemas']['FileView'];
export type DocumentSettings = components['schemas']['DocumentSettingsView'];
export type FileOwner =
  | 'quote'
  | 'invoice'
  | 'job'
  | 'trading_entity'
  | 'safety_document'
  /** FIN-02 at v1.993: a credit's adjustment note. */
  | 'credit_note';

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

export const filesQuery = (owner: FileOwner, id: string) =>
  queryOptions({
    queryKey: ['files', owner, id],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/files', { params: { query: { owner, id } } });
      if (error) throw problem(error, 'Could not load the documents.');
      return data;
    },
  });

export async function renderQuoteDocument(quoteId: string) {
  const { data, error } = await api.POST('/v1/quotes/{id}/documents', {
    params: { path: { id: quoteId } },
  });
  if (error) throw problem(error, 'Could not prepare the quote.');
  return data;
}

export async function renderInvoiceDocument(invoiceId: string) {
  const { data, error } = await api.POST('/v1/invoices/{id}/documents', {
    params: { path: { id: invoiceId } },
  });
  if (error) throw problem(error, 'Could not prepare the invoice.');
  return data;
}

/** FIN-02 at v1.993: a credit as its adjustment note. */
export async function renderCreditNoteDocument(creditNoteId: string) {
  const { data, error } = await api.POST('/v1/credit-notes/{id}/documents', {
    params: { path: { id: creditNoteId } },
  });
  if (error) throw problem(error, 'Could not prepare the adjustment note.');
  return data;
}

export async function renderJobDocument(jobId: string, kind: 'pick_list' | 'docket') {
  const { data, error } = await api.POST('/v1/jobs/{id}/documents', {
    params: { path: { id: jobId } },
    body: { kind },
  });
  if (error) throw problem(error, 'Could not prepare that document.');
  return data;
}

/** CMP-02 / CMP-04 at v1.967: the current version, printed with a sign-on sheet. */
export async function renderSafetyDocument(id: string) {
  const { data, error } = await api.POST('/v1/safety-documents/{id}/documents', {
    params: { path: { id } },
  });
  if (error) throw problem(error, 'Could not print it.');
  return data;
}

/** Multipart: the browser sets the boundary, so the body goes as it is. */
function form(file: File): FormData {
  const data = new FormData();
  data.append('file', file);
  return data;
}

export async function uploadToJob(jobId: string, file: File) {
  const { data, error } = await api.POST('/v1/jobs/{id}/files', {
    params: { path: { id: jobId } },
    body: form(file) as never,
    bodySerializer: (body) => body as unknown as FormData,
  });
  if (error) throw problem(error, 'Could not upload that file.');
  return data;
}

/** Asks for a link now and follows it: the download starts, and the link is never kept. */
export async function openFile(fileId: string): Promise<void> {
  const { data, error } = await api.GET('/v1/files/{id}/link', {
    params: { path: { id: fileId } },
  });
  if (error) throw problem(error, 'Could not open that file.');
  window.location.assign(data.url);
}

export const documentSettingsQuery = queryOptions({
  queryKey: ['document-settings'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/document-settings');
    if (error) throw problem(error, 'Could not load the document settings.');
    return data;
  },
});

export async function saveTerms(entityId: string, body: string) {
  const { data, error } = await api.PUT('/v1/document-settings/{entityId}/terms', {
    params: { path: { entityId } },
    body: { body },
  });
  if (error) throw problem(error, 'Could not save the terms.');
  return data;
}

export async function updateEntityContact(
  entityId: string,
  changes: {
    address?: string | null;
    phone?: string | null;
    email?: string | null;
    paymentDetails?: string | null;
  },
) {
  const { data, error } = await api.PATCH('/v1/document-settings/{entityId}/contact', {
    params: { path: { entityId } },
    body: changes,
  });
  if (error) throw problem(error, 'Could not save those details.');
  return data;
}

export async function uploadLogo(entityId: string, file: File) {
  const { data, error } = await api.POST('/v1/document-settings/{entityId}/logo', {
    params: { path: { entityId } },
    body: form(file) as never,
    bodySerializer: (body) => body as unknown as FormData,
  });
  if (error) throw problem(error, 'Could not upload the logo.');
  return data;
}
